/**
 * useEAAssistant Hook
 * State management for EA Assistant feature
 *
 * Handles:
 * - QBridge connection status
 * - EA file list fetching
 * - File selection and content loading
 * - Cost estimation
 * - Analysis flow
 * - Code modification
 * - Save operation
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  checkQBridgeStatus,
  getEAList,
  getEAContent,
  getEACost,
  analyzeEA,
  modifyEA,
  saveEAModified,
  validateEAFileSize,
  EA_ANALYSIS_COST,
  EA_MODIFY_COST
} from '../api/qbridgeEaApi';
import { getAnalysisHistory } from '../api/eaAnalysisApi';

/**
 * View states for the EA Assistant flow
 */
export const EA_VIEWS = {
  LIST: 'list',           // File list view
  COST_DIALOG: 'cost',    // Cost estimate dialog
  ANALYZING: 'analyzing', // Analysis in progress
  RESULTS: 'results',     // Analysis results view
  DIFF: 'diff',           // Code diff view
  SAVING: 'saving'        // Save in progress
};

/**
 * useEAAssistant Hook
 * @param {string} token - JWT authentication token
 * @returns {object} EA Assistant state and handlers
 */
const useEAAssistant = (token) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════

  // QBridge connection
  const [qbridgeOnline, setQbridgeOnline] = useState(false);
  const [qbridgeVersion, setQbridgeVersion] = useState(null);

  // File list
  const [files, setFiles] = useState([]);
  const [fileSummary, setFileSummary] = useState({
    experts_count: 0,
    indicators_count: 0,
    scripts_count: 0
  });
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState(null);

  // Analysis history (files that have been analyzed before)
  const [analyzedFiles, setAnalyzedFiles] = useState([]);

  // Selected file
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState(null);

  // Cost estimate
  const [estimatedCost, setEstimatedCost] = useState(EA_ANALYSIS_COST);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [hasSufficientBalance, setHasSufficientBalance] = useState(true);
  const [purchaseUrl, setPurchaseUrl] = useState(null);
  const [isLoadingCost, setIsLoadingCost] = useState(false);
  const [costError, setCostError] = useState(null);

  // Consent state
  const [consentGiven, setConsentGiven] = useState(false);

  // File size validation
  const [fileSizeValid, setFileSizeValid] = useState(true);
  const [fileSizeError, setFileSizeError] = useState(null);

  // Analysis
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [balanceRemaining, setBalanceRemaining] = useState(0);

  // Modifications
  const [modifiedSource, setModifiedSource] = useState(null);
  const [modifiedIncludes, setModifiedIncludes] = useState(null);

  // Save
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Current view state
  const [currentView, setCurrentView] = useState(EA_VIEWS.LIST);

  // Dialog visibility
  const [showCostDialog, setShowCostDialog] = useState(false);

  // Refs for polling
  const qbridgeCheckInterval = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // QBRIDGE STATUS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check QBridge connection status
   */
  const checkQBridge = useCallback(async () => {
    try {
      const status = await checkQBridgeStatus();
      setQbridgeOnline(status.online);
      setQbridgeVersion(status.version);
      return status.online;
    } catch (error) {
      setQbridgeOnline(false);
      return false;
    }
  }, []);

  // Check QBridge status on mount and periodically
  useEffect(() => {
    // Initial check
    checkQBridge();

    // Poll every 30 seconds
    qbridgeCheckInterval.current = setInterval(checkQBridge, 30000);

    return () => {
      if (qbridgeCheckInterval.current) {
        clearInterval(qbridgeCheckInterval.current);
      }
    };
  }, [checkQBridge]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FILE LIST
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Fetch EA file list from QBridge
   */
  const fetchFiles = useCallback(async (refresh = false) => {
    if (!qbridgeOnline && !refresh) {
      setFilesError('QBridge is not running');
      return;
    }

    setIsLoadingFiles(true);
    setFilesError(null);

    try {
      const result = await getEAList({ type: 'all', refresh });

      if (result.success) {
        setFiles(result.files);
        setFileSummary(result.summary);
        setFilesError(null);
      } else {
        setFilesError(result.error || 'Failed to load files');
      }
    } catch (error) {
      setFilesError(error.message || 'Failed to fetch files');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [qbridgeOnline]);

  // Fetch files when QBridge comes online
  useEffect(() => {
    if (qbridgeOnline && files.length === 0) {
      fetchFiles();
    }
  }, [qbridgeOnline, files.length, fetchFiles]);

  /**
   * Refresh file list
   */
  const refreshFiles = useCallback(() => {
    fetchFiles(true);
  }, [fetchFiles]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYSIS HISTORY
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Fetch analysis history to show badges for previously analyzed files
   * Backend identifies user from JWT token (no userId param needed)
   */
  const fetchAnalysisHistory = useCallback(async () => {
    if (!token) return;

    try {
      // Backend uses token for user identification, not userId in path
      const result = await getAnalysisHistory({ limit: 50 });
      if (result.success) {
        // Extract file names from history records
        const fileNames = Array.isArray(result.data)
          ? result.data.map(item => typeof item === 'string' ? item : item.fileName)
          : [];
        setAnalyzedFiles(fileNames);
      }
    } catch (error) {
      console.error('[useEAAssistant] Failed to fetch analysis history:', error);
    }
  }, [token]);

  // Fetch analysis history when token is available
  useEffect(() => {
    if (token) {
      fetchAnalysisHistory();
    }
  }, [token, fetchAnalysisHistory]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FILE SELECTION & CONTENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Select a file from the list
   */
  const selectFile = useCallback((file) => {
    setSelectedFile(file);
    // Reset analysis state when selecting new file
    setAnalysisResult(null);
    setModifiedSource(null);
    setModifiedIncludes(null);
    setSaveResult(null);
    setSaveError(null);
    setContentError(null);
    setCurrentView(EA_VIEWS.LIST);
  }, []);

  /**
   * Load file content for analysis
   */
  const loadFileContent = useCallback(async (file) => {
    if (!token) {
      setContentError('Authentication required');
      return null;
    }

    setIsLoadingContent(true);
    setContentError(null);
    setFileSizeValid(true);
    setFileSizeError(null);

    try {
      const result = await getEAContent(token, file.name, true);

      if (result.success) {
        // Validate file size (500KB limit)
        const sizeValidation = validateEAFileSize(result.main_source);
        if (!sizeValidation.valid) {
          setFileSizeValid(false);
          setFileSizeError(sizeValidation.error);
          setContentError(sizeValidation.error);
          return null;
        }

        setFileContent(result);
        setFileSizeValid(true);
        return result;
      } else {
        setContentError(result.error);
        return null;
      }
    } catch (error) {
      setContentError(error.message);
      return null;
    } finally {
      setIsLoadingContent(false);
    }
  }, [token]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COST ESTIMATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Open cost estimate dialog
   * @param {string} operation - 'analyze' or 'modify'
   * @param {Array} modifications - Optional modifications array for modify cost calculation
   */
  const openCostDialog = useCallback(async (operation = 'analyze', modifications = []) => {
    if (!selectedFile || !token) return;

    setShowCostDialog(true);
    setIsLoadingCost(true);
    setCostError(null);
    setConsentGiven(false); // Reset consent when opening dialog

    try {
      // Build cost request payload matching Backend API spec
      const costData = {
        lineCount: selectedFile.line_count || fileContent?.line_count || 0,
        modifications: operation === 'modify' ? modifications : []
      };

      const result = await getEACost(token, costData);

      if (result.success) {
        setEstimatedCost(result.estimatedCost);
        setCurrentBalance(result.currentBalance);
        setHasSufficientBalance(result.hasSufficientBalance);
        setPurchaseUrl(result.purchaseUrl);
      } else {
        // Use default cost on error
        const defaultCost = operation === 'modify' ? EA_MODIFY_COST : EA_ANALYSIS_COST;
        setEstimatedCost(defaultCost);
        setHasSufficientBalance(true); // Let API handle 402
      }
    } catch (error) {
      const defaultCost = operation === 'modify' ? EA_MODIFY_COST : EA_ANALYSIS_COST;
      setEstimatedCost(defaultCost);
    } finally {
      setIsLoadingCost(false);
    }
  }, [selectedFile, token, fileContent]);

  /**
   * Close cost dialog
   */
  const closeCostDialog = useCallback(() => {
    setShowCostDialog(false);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start EA analysis
   * @param {boolean} userConsentGiven - Whether user has given consent (from dialog)
   */
  const startAnalysis = useCallback(async (userConsentGiven = false) => {
    if (!selectedFile || !token) return;

    // Validate consent
    const consent = userConsentGiven || consentGiven;
    if (!consent) {
      setAnalysisError({
        type: 'consent_required',
        message: 'User consent is required to analyze EA code'
      });
      return;
    }

    setShowCostDialog(false);
    setIsAnalyzing(true);
    setAnalysisError(null);
    setCurrentView(EA_VIEWS.ANALYZING);

    try {
      // Load content if not already loaded
      let content = fileContent;
      if (!content) {
        content = await loadFileContent(selectedFile);
        if (!content) {
          throw new Error('Failed to load file content');
        }
      }

      // Validate file size before API call
      if (!fileSizeValid) {
        throw new Error(fileSizeError || 'File too large. Maximum size is 500KB.');
      }

      // Debug log content before API call
      console.log('[useEAAssistant] Starting analysis with content:', {
        fileName: selectedFile.name,
        mainSourceLength: content.main_source?.length || 0,
        mainSourcePreview: content.main_source?.substring(0, 100) || 'EMPTY',
        includesCount: content.includes ? Object.keys(content.includes).length : 0
      });

      // Call analysis API with consent
      const result = await analyzeEA(token, {
        name: selectedFile.name,
        mainSource: content.main_source,
        includeFiles: content.includes,
        consentGiven: true
      });

      if (result.success) {
        setAnalysisResult(result.analysis);
        setTokensUsed(result.tokensUsed);
        setBalanceRemaining(result.balanceRemaining);
        setCurrentView(EA_VIEWS.RESULTS);

        // If analysis includes modifications, store them
        if (result.analysis?.modifiedSource) {
          setModifiedSource(result.analysis.modifiedSource);
          setModifiedIncludes(result.analysis.modifiedIncludes || {});
        }
      } else {
        // Handle specific error codes
        if (result.errorCode === 402) {
          setAnalysisError({
            type: 'insufficient_balance',
            message: 'Insufficient token balance',
            required: result.required,
            available: result.available,
            purchaseUrl: result.purchaseUrl
          });
        } else if (result.errorCode === 401) {
          setAnalysisError({
            type: 'auth',
            message: result.error
          });
        } else if (result.errorCode === 408) {
          setAnalysisError({
            type: 'timeout',
            message: result.error || 'Analysis timed out. Please try again.'
          });
        } else if (result.errorCode === 413) {
          setAnalysisError({
            type: 'file_too_large',
            message: result.error || 'File too large. Maximum size is 500KB.'
          });
        } else {
          setAnalysisError({
            type: 'general',
            message: result.error
          });
        }
        setCurrentView(EA_VIEWS.LIST);
      }
    } catch (error) {
      setAnalysisError({
        type: 'general',
        message: error.message || 'Analysis failed'
      });
      setCurrentView(EA_VIEWS.LIST);
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedFile, token, fileContent, loadFileContent, consentGiven, fileSizeValid, fileSizeError]);

  // ═══════════════════════════════════════════════════════════════════════════
  // MODIFICATIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Request modifications from Q.AI
   * @param {Array<{type: string, bugs?: number[], feature?: string}>|string} modifications - Modifications to apply or legacy instruction string
   * @param {boolean} userConsentGiven - Whether user has given consent
   */
  const requestModifications = useCallback(async (modifications, userConsentGiven = false) => {
    if (!selectedFile || !token || !fileContent) return;

    // Validate consent
    const consent = userConsentGiven || consentGiven;
    if (!consent) {
      setAnalysisError({
        type: 'consent_required',
        message: 'User consent is required to modify EA code'
      });
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      // Build payload - support both new modifications array and legacy instruction string
      const payload = {
        name: selectedFile.name,
        mainSource: fileContent.main_source,
        consentGiven: true
      };

      if (typeof modifications === 'string') {
        // Legacy: instruction string
        payload.instruction = modifications;
      } else {
        // New: modifications array
        payload.modifications = modifications;
      }

      const result = await modifyEA(token, payload);

      if (result.success) {
        setModifiedSource(result.modifiedSource);
        setModifiedIncludes(result.modifiedIncludes);
        setTokensUsed(prev => prev + result.tokensUsed);
        setBalanceRemaining(result.balanceRemaining);
        setCurrentView(EA_VIEWS.DIFF);
      } else {
        if (result.errorCode === 402) {
          setAnalysisError({
            type: 'insufficient_balance',
            message: 'Insufficient token balance',
            required: result.required,
            available: result.available,
            purchaseUrl: result.purchaseUrl
          });
        } else if (result.errorCode === 408) {
          setAnalysisError({
            type: 'timeout',
            message: result.error || 'Modification timed out. Please try again.'
          });
        } else {
          setAnalysisError({
            type: 'general',
            message: result.error
          });
        }
      }
    } catch (error) {
      setAnalysisError({
        type: 'general',
        message: error.message || 'Modification failed'
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedFile, token, fileContent, consentGiven]);

  /**
   * View code changes (switch to diff view)
   */
  const viewCodeChanges = useCallback(() => {
    setCurrentView(EA_VIEWS.DIFF);
  }, []);

  /**
   * Go back to results from diff view
   */
  const backToResults = useCallback(() => {
    setCurrentView(EA_VIEWS.RESULTS);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // SAVE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Save modified EA as _QAI version
   */
  const saveModifiedEA = useCallback(async () => {
    if (!selectedFile || !token || !modifiedSource || !fileContent) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveResult(null);

    try {
      const result = await saveEAModified(token, {
        originalName: selectedFile.name,
        modifiedSource: modifiedSource,
        modifiedIncludes: modifiedIncludes || {}
      });

      if (result.success) {
        setSaveResult(result);
        // Refresh file list to show new _QAI file
        refreshFiles();
      } else {
        if (result.errorCode === 409) {
          setSaveError('File was modified externally. Please refresh and try again.');
        } else {
          setSaveError(result.error || 'Save failed');
        }
      }
    } catch (error) {
      setSaveError(error.message || 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [selectedFile, token, modifiedSource, modifiedIncludes, fileContent, refreshFiles]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RESET
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Reset all state (go back to file list)
   */
  const resetState = useCallback(() => {
    setSelectedFile(null);
    setFileContent(null);
    setAnalysisResult(null);
    setModifiedSource(null);
    setModifiedIncludes(null);
    setSaveResult(null);
    setSaveError(null);
    setAnalysisError(null);
    setContentError(null);
    setCostError(null);
    setConsentGiven(false);
    setFileSizeValid(true);
    setFileSizeError(null);
    setCurrentView(EA_VIEWS.LIST);
    setShowCostDialog(false);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════════

  return {
    // QBridge status
    qbridgeOnline,
    qbridgeVersion,
    checkQBridge,

    // File list
    files,
    fileSummary,
    isLoadingFiles,
    filesError,
    fetchFiles,
    refreshFiles,

    // Analysis history
    analyzedFiles,
    fetchAnalysisHistory,

    // Selected file
    selectedFile,
    selectFile,
    fileContent,
    isLoadingContent,
    contentError,
    loadFileContent,

    // File size validation
    fileSizeValid,
    fileSizeError,

    // Cost estimate
    showCostDialog,
    estimatedCost,
    currentBalance,
    hasSufficientBalance,
    purchaseUrl,
    isLoadingCost,
    costError,
    openCostDialog,
    closeCostDialog,

    // Consent
    consentGiven,
    setConsentGiven,

    // Analysis
    analysisResult,
    isAnalyzing,
    analysisError,
    tokensUsed,
    balanceRemaining,
    startAnalysis,

    // Modifications
    modifiedSource,
    modifiedIncludes,
    requestModifications,
    viewCodeChanges,
    backToResults,
    hasModifications: !!modifiedSource,

    // Save
    isSaving,
    saveResult,
    saveError,
    saveModifiedEA,

    // View state
    currentView,
    setCurrentView,

    // Reset
    resetState
  };
};

export default useEAAssistant;
